import type { RangePickerProps } from 'antd/lib/date-picker';
import type { Dayjs } from 'dayjs';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import { Divider, Flex, Space } from '@/components/base';
import Button from '@/components/Button/Button';
import StyledDatePicker from '@/components/DatePicker';
import { useModalContext } from '@/components/Modal/Modal';
import type { SelectOptionT } from '@/components/Select/types';
import Heading from '@/components/Typography/Heading';
import Typo from '@/components/Typography/Typo';
import useColor from '@/hooks/domain/useColor';
import service from '@/services/index';
import { modalStyle } from '@/styles/style';
import { getTimezoneDayjs } from '@/utils/time';

import type {
  SalesforceInfo,
  SyncConditionInfo,
} from '../types';

type SalesforceImportConversationsModalProps = {
  organization: SalesforceInfo;
  onImport: (payload: {
    accountSync: boolean;
    taskSync: boolean;
    startDate?: string;
    endDate?: string;
    syncConditionInfo?: SyncConditionInfo[];
  }) => Promise<void>;
};

type SalesforceImportConversationState = {
  startDate: Dayjs | null;
  endDate: Dayjs | null;
};

const SalesforceImportConversationsModal = (
  props: SalesforceImportConversationsModalProps,
) => {
  const color = useColor();
  const { closeModal } = useModalContext();
  const { organization } = props;

  const accountSync = true;
  const [allTaskSync, setAllTaskSync] = useState(
    !organization.sync_condition_info,
  );
  const [taskKeyOptions, setTaskKeyOptions] = useState<SelectOptionT[]>([]);
  const [taskKey, setTaskKey] = useState('');
  const [taskValue, setTaskValue] = useState<boolean | null>(null);

  const [paritalTaskSync, setPartialTaskSync] = useState(
    !!organization.sync_condition_info,
  );

  const historyList = organization.history || [];
  const hasHistory = historyList.length > 0;

  const [state, setState] = useState<SalesforceImportConversationState>({
    startDate: null,
    endDate: hasHistory ? null : getTimezoneDayjs(),
  });

  useEffect(() => {
    if (allTaskSync) {
      setPartialTaskSync(false);
      setTaskKeyOptions([]);
      setTaskKey('');
      setTaskValue(null);
    }
  }, [allTaskSync]);

  useEffect(() => {
    const getTaskFieldInfo = async () => {
      const data = await service.salesforce.getSalesforceTaskFieldInfo(
        organization.alias,
      );
      setTaskKeyOptions(
        data.map((d: SyncConditionInfo) => ({
          key: d.key,
          node: d.label,
          searchName: d.label,
        })),
      );
      if (organization.sync_condition_info) {
        if (organization.sync_condition_info?.length > 0) {
          setTaskKey(organization.sync_condition_info[0].key);
          setTaskValue(organization.sync_condition_info[0].value as boolean);
        }
      }
    };
    if (paritalTaskSync) {
      setAllTaskSync(false);
      getTaskFieldInfo();
    }
  }, [organization.alias, organization.sync_condition_info, paritalTaskSync]);

  const disabledStartDate: RangePickerProps['disabledDate'] = (current) => {
    const future = current > getTimezoneDayjs().endOf('day');
    if (!state.endDate) return future;
    else {
      const futureToEndDate = current > state.endDate.endOf('day');
      const before90DaysPriorToEndDate =
        current < state.endDate.addVal(-89, 'day').startOf('day');
      return future || before90DaysPriorToEndDate || futureToEndDate;
    }
  };

  const disabledEndDate: RangePickerProps['disabledDate'] = (current) => {
    const future = current > getTimezoneDayjs().endOf('day');
    if (!state.startDate) return future;
    else {
      const pastToStartDate = current < state.startDate.startOf('day');
      const after90DaysFromStartDate =
        current > state.startDate.addVal(89, 'day').endOf('day');
      return future || after90DaysFromStartDate || pastToStartDate;
    }
  };

  // const importCheck = useMemo(() => {
  //   if (allTaskSync) {
  //     return state.startDate && state.endDate;
  //   } else if (paritalTaskSync) {
  //     return state.startDate && state.endDate && taskKey && taskValue !== null;
  //   }

  //   return !organization.accountSync && accountSync;
  // }, [
  //   organization,
  //   accountSync,
  //   allTaskSync,
  //   state,
  //   paritalTaskSync,
  //   taskKey,
  //   taskValue,
  // ]);

  const handleImportSalesforceInfo = async () => {
    let taskSyncInfo = [];
    if (taskKey && taskValue !== null) {
      const option = taskKeyOptions.find((t) => t.value === taskKey);
      if (option) {
        taskSyncInfo.push({
          key: taskKey,
          label: option.searchName,
          type: 'boolean',
          value: taskValue,
        });
      }
    }
    await props.onImport({
      accountSync: organization.account_sync ? false : accountSync,
      taskSync: allTaskSync || paritalTaskSync,
      startDate: state.startDate
        ? getTimezoneDayjs(state.startDate).toJSON()
        : undefined,
      endDate: state.endDate ? getTimezoneDayjs(state.endDate).toJSON() : undefined,
      syncConditionInfo: taskSyncInfo,
    });

    closeModal();
  };

  return (
    <ImportConversationModalStyle d="column">
      <Flex d="column" g="32px">
        <Heading type="h2">Import data options</Heading>
        <Flex d="column" g="24px">
          {/* {!organization.accountSync && (
              <>
                <Flex d="column" g="8px">
                  <Flex g="8px">
                    <Checkbox
                      checked={accountSync}
                      onChange={() => {
                        setAccountSync((state) => !state);
                      }}
                      size={20}
                    />
                    <Text type="label1">Contact</Text>
                  </Flex>
                  <Text
                    type="label2"
                    color={color.gray7}
                    style={{ paddingLeft: 6 }}
                  >
                    It includes all the accounts, contacts, and leads in
                    Salesforce.
                  </Text>
                </Flex>
                <Divider direction="h" />
              </>
            )} */}
          {/* <Flex d="column" g="8px">
              <Flex g="8px">
                <Checkbox
                  checked={allTaskSync}
                  onChange={() => {
                    setAllTaskSync((state) => !state);
                  }}
                  size={20}
                />
                <Text type="label1">Task</Text>
              </Flex>
              <Text
                type="label2"
                color={color.gray700}
                style={{ paddingLeft: 6 }}
              >
                It includes activities related to all the accounts, contacts,
                and leads.
              </Text>
            </Flex> */}
          {/* <Flex g="8px">
              <Checkbox
                checked={allTaskSync}
                onChange={() => {
                  setAllTaskSync((state) => !state);
                }}
                size={20}
              />
              <Text type="label1">All Task</Text>
            </Flex> */}
          {/* <Flex d="column">
              <Flex g="8px">
                <Checkbox
                  checked={paritalTaskSync}
                  onChange={() => {
                    setPartialTaskSync((state) => !state);
                  }}
                  size={20}
                />
                <Text type="label1">Partial Task</Text>
              </Flex>

              {taskKeyOptions.length > 0 && (
                <Flex d="column" g="4px">
                  <Space h="12px" />
                  <Text type="label1" color={color.gray8}>
                    Select a field from which you want to retrieve data
                  </Text>
                  <SelectDropdown
                    width={'562px'}
                    pos={{ top: 32, left: 0 }}
                    defaultSelectedStyle={{
                      width: '100%',
                      height: 28,
                      paddingLeft: 8,
                      border: `1px solid ${color.gray4}`,
                    }}
                    name={`task-field-key`}
                    selectedItem={taskKey}
                    selected={
                      taskKeyOptions.find((option) => option.key === taskKey)
                        ?.key || ''
                    }
                    onSelect={(key) => {
                      setTaskKey(key as string);
                    }}
                    options={taskKeyOptions}
                    placeholder="Select field"
                  />
                  {taskKey && (
                    <SelectDropdown
                      width="562px"
                      pos={{ top: 32, left: 0 }}
                      defaultSelectedStyle={{
                        width: '100%',
                        height: 28,
                        paddingLeft: 8,
                        border: `1px solid ${color.gray4}`,
                      }}
                      name={`task-field-value`}
                      selectedItem={taskValue === null ? '' : taskValue ? 1 : 2}
                      selected={
                        taskValue === null ? '' : taskValue ? 'True' : 'False'
                      }
                      onSelect={(key) => {
                        setTaskValue(key === 1);
                      }}
                      options={BOOLEAN_LIST}
                      placeholder="Select value"
                    />
                  )}
                </Flex>
              )}
            </Flex> */}
        </Flex>
        <Flex d="column" g="4px" style={{ paddingLeft: 3 }}>
          <Typo type="label1">Date</Typo>
          <Typo type="label3" color={color.gray800}>
            This is a one-time import. You can repeat the process with a
            different date range if needed.
          </Typo>
          <Typo type="label3" color={color.gray800}>
            The maximum duration is 90 days.
          </Typo>
          <Flex></Flex>
          <Flex a="center" g="12px">
            <StyledDatePicker
              style={{
                border: `1px solid ${color.gray400}`,
                borderRadius: '8px',
              }}
              name="startDate"
              onChange={(_, dateString) =>
                setState((prev) => ({
                  ...prev,
                  startDate: getTimezoneDayjs(dateString),
                }))
              }
              disabledDate={disabledStartDate}
              getPopupContainer={(triggerNode) => {
                return triggerNode.parentNode as HTMLDivElement;
              }}
              value={state.startDate}
            />
            <Typo type="label2" color={color.gray600}>
              -
            </Typo>
            {hasHistory ? (
              <StyledDatePicker
                style={{
                  border: `1px solid ${color.gray400}`,
                  borderRadius: '8px',
                }}
                name="endDate"
                onChange={(_, dateString) =>
                  setState((prev) => ({
                    ...prev,
                    endDate: getTimezoneDayjs(dateString),
                  }))
                }
                disabledDate={disabledEndDate}
                getPopupContainer={(triggerNode) => {
                  return triggerNode.parentNode as HTMLDivElement;
                }}
                value={state.endDate}
              />
            ) : (
              <Typo type="label2" color={color.gray600}>
                Today
              </Typo>
            )}
          </Flex>
          {hasHistory && (
            <>
              <Space h="24px" />
              <Flex d="column" g="8px">
                <Heading type="h3">One-time import history</Heading>
                {historyList.map((historyItem) => {
                  const startDate = getTimezoneDayjs(
                    historyItem.start_date || undefined,
                  ).format('YYYY/MM/DD');
                  const endDate = getTimezoneDayjs(
                    historyItem.end_date || undefined,
                  ).format('YYYY/MM/DD');
                  const createdDate = getTimezoneDayjs(
                    historyItem.created_at,
                  ).format('YYYY/MM/DD');
                  return (
                    <>
                      <Divider direction="h" />
                      <Flex j="space-between">
                        <Typo type="label2">
                          From {startDate} to {endDate}
                        </Typo>
                        <Typo type="label3" color={color.gray600}>
                          imported on {createdDate}
                        </Typo>
                      </Flex>
                    </>
                  );
                })}
                <Divider direction="h" />
              </Flex>
            </>
          )}
        </Flex>
      </Flex>
      <Space h="32px" />
      <Flex j="flex-end">
        <Button onClick={handleImportSalesforceInfo}>
          {organization.import_state === 0 ? 'Import' : 'Update'}
        </Button>
      </Flex>
    </ImportConversationModalStyle>
  );
};

const ImportConversationModalStyle = styled(Flex)`
  width: 620px;
  padding: 28px;
  ${modalStyle};
`;

export default SalesforceImportConversationsModal;
